import React, {useEffect, useMemo, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faArrowRight,
    faBinoculars,
    faChartSimple,
    faCompass,
    faLocationDot,
    faRoute,
    faTableCellsLarge,
    faTrophy,
    faUser
} from "@fortawesome/free-solid-svg-icons";
import {useNavigate} from "react-router-dom";
import {getRandomDashboardHeroImage} from "../../../helpers/dashboardHeroImages";
import {
    getBigYearOptions,
    getDefaultBigYear,
    getUniqueChecklistCount,
    getUniqueSpeciesCount
} from "../helpers";
import type {DiscoverProps} from "../types";
import './discover-landing.scss';

export default function DiscoverLanding({rawData, lifeList, years, onStartBigYear}: DiscoverProps) {
    const navigate = useNavigate();
    const heroImage = useMemo(() => getRandomDashboardHeroImage(), []);
    const totalSpecies = getUniqueSpeciesCount(lifeList);
    const totalChecklists = getUniqueChecklistCount(rawData);
    const locationCount = new Set(rawData.map(observation => observation.locationId || observation.location).filter(Boolean)).size;
    const bigYearOptions = useMemo(() => getBigYearOptions(years), [years]);
    const defaultBigYear = useMemo(() => getDefaultBigYear(years), [years]);
    const [selectedBigYear, setSelectedBigYear] = useState(defaultBigYear);

    useEffect(() => {
        setSelectedBigYear(defaultBigYear);
    }, [defaultBigYear]);

    const tiles = [
        {
            title: 'My Big Year',
            label: 'Available',
            icon: faChartSimple,
            description: 'Relive your own Big Year as a cinematic experience.',
            action: selectedBigYear === 'life' ? 'View my Life List Big Year' : `View my ${selectedBigYear} Big Year`,
            onClick: () => onStartBigYear(selectedBigYear),
            control: (
                <label className="discover-tile__select">
                    <span>Year</span>
                    <select value={selectedBigYear} onChange={event => setSelectedBigYear(event.target.value)}>
                        {bigYearOptions.map(option => (
                            <option value={option.value} key={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>
            )
        },
        {
            title: 'My Life Journey',
            label: 'Preview',
            icon: faRoute,
            description: 'A timeline of milestones, firsts, trips, and totals from your birding life.',
            action: 'View Journey',
            onClick: () => navigate('/discover/life-journey')
        },
        {
            title: 'Achievements',
            label: 'Preview',
            icon: faTrophy,
            description: 'Celebrate milestones worth keeping in view.',
            action: 'View Achievements',
            onClick: () => navigate('/discover/achievements')
        },
        {
            title: 'Discovery Explorer',
            label: 'Coming Soon',
            icon: faCompass,
            description: 'Find patterns, gaps, and new ways to explore your sightings.',
            action: 'Coming Soon'
        },
        {
            title: 'Your Birding Personality',
            label: 'Coming Soon',
            icon: faUser,
            description: 'Turn your habits into a readable profile of how you bird.',
            action: 'Coming Soon'
        }
    ];

    return (
        <div className="discover">
            <section className="discover-intro" style={{'--discover-hero-image': `url(${heroImage})`} as React.CSSProperties}>
                <div className="discover-intro__content">
                    <p className="discover__eyebrow">Discover</p>
                    <h1>Your Birding Life History</h1>
                    <p>Discover new ways to revisit your sightings, learn more about yourself and the birds you've seen, and spark your next personal birding challenge.</p>
                    <dl>
                        <div>
                            <span className="discover-intro__stat-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faBinoculars} />
                            </span>
                            <dt>{totalSpecies.toLocaleString()}</dt>
                            <dd>Species Seen</dd>
                        </div>
                        <div>
                            <span className="discover-intro__stat-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faTableCellsLarge} />
                            </span>
                            <dt>{totalChecklists.toLocaleString()}</dt>
                            <dd>Checklists</dd>
                        </div>
                        <div>
                            <span className="discover-intro__stat-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faLocationDot} />
                            </span>
                            <dt>{locationCount.toLocaleString()}</dt>
                            <dd>Locations</dd>
                        </div>
                    </dl>
                </div>
            </section>

            <section className="discover-grid" aria-label="Discover views">
                {tiles.map(tile => (
                    <article className={`discover-tile ${tile.onClick ? '' : 'is-mocked'}`} key={tile.title}>
                        <div className="discover-tile__header">
                            <span className="discover-tile__icon" aria-hidden="true">
                                <FontAwesomeIcon icon={tile.icon} />
                            </span>
                            <span className="discover-tile__label">{tile.label}</span>
                        </div>
                        <h2>{tile.title}</h2>
                        <p>{tile.description}</p>
                        {'control' in tile ? tile.control : null}
                        <button type="button" onClick={tile.onClick} disabled={!tile.onClick}>
                            {tile.action}
                            <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                        </button>
                    </article>
                ))}
            </section>
        </div>
    );
}
